import { Link, useLocation, useNavigate } from 'react-router-dom'
import { BriefcaseBusiness, LogOut, User, ChevronDown } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { usePlatformSettings } from '@/hooks/usePlatformSettings'
import { PLATFORM_URLS } from '@/lib/env'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { useState } from 'react'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog'

const navItems = [
  { label: '首页', path: '/' },
  { label: '找工作', path: '/jobs' },
  { label: '找企业', path: '/enterprise' },
  { label: '找院校', path: '/edu' },
  { label: '人才库', path: '/talent', requireLogin: true },
  { label: '招聘会', path: '/jobfair' },
  { label: '政策资讯', path: '/policy' },
  { label: '招聘公告', path: '/notice' },
  { label: '创业项目', path: '/entrepreneurship' },
]

export default function Header() {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { isLoggedIn, logout } = useAuth()
  const { platformName, platformLogo } = usePlatformSettings()
  const [loginAlert, setLoginAlert] = useState(false)

  const displayRole = localStorage.getItem('original_role') || localStorage.getItem('role') || ''
  const displayName = displayRole === 'admin' ? '管理员' : displayRole === 'school' ? '学校用户' : displayRole === 'enterprise' ? '企业用户' : '个人中心'

  const isActive = (path: string) => {
    if (path === '/') return pathname === '/'
    return pathname.startsWith(path)
  }

  const handleNavClick = (item: typeof navItems[0]) => {
    if (item.requireLogin && !isLoggedIn) {
      setLoginAlert(true)
    } else {
      navigate(item.path)
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  return (
    <>
      <header className="sticky top-0 z-50 glass-header border-b border-white/60 rounded-b-[20px] shadow-[0_4px_24px_rgba(54,98,236,0.08)]">
        <div className="max-w-[1280px] mx-auto px-8 h-[76px] flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <div className="w-[38px] h-[38px] rounded-[10px] gradient-primary shadow-glow flex items-center justify-center overflow-hidden">
              {platformLogo ? (
                <img src={platformLogo} alt={`${platformName} logo`} className="h-full w-full object-contain bg-white p-1" />
              ) : (
                <BriefcaseBusiness className="w-5 h-5 text-white" />
              )}
            </div>
            <span className="text-lg font-bold text-gradient">{platformName}</span>
          </Link>

          {/* Navigation */}
          <nav className="hidden xl:flex items-center h-[72px] gap-0">
            {navItems.map(item => (
              <button
                key={item.path}
                onClick={() => handleNavClick(item)}
                className={`h-[72px] px-3.5 flex flex-col items-center justify-center rounded-[10px] text-sm transition-colors cursor-pointer ${
                  isActive(item.path)
                    ? 'bg-primary-light text-[#3662EC] font-semibold'
                    : 'text-text-secondary hover:text-text-primary hover:bg-gray-50'
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-3 shrink-0">
            {isLoggedIn ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-2 px-3 py-2 rounded-[10px] hover:bg-gray-50 transition-colors cursor-pointer outline-none">
                  <div className="w-8 h-8 rounded-full gradient-primary flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm text-text-body font-medium">{displayName}</span>
                  <ChevronDown className="w-4 h-4 text-text-placeholder" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40">
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                    <User className="w-4 h-4 mr-2" />个人中心
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-error">
                    <LogOut className="w-4 h-4 mr-2" />退出登录
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login" className="px-4 py-2 rounded-[10px] border border-[#E5E7EB] text-sm text-text-body font-medium hover:bg-gray-50 transition-colors">
                  登录
                </Link>
                <Link to="/register" className="px-5 py-2.5 rounded-[10px] gradient-primary shadow-glow text-sm text-white font-semibold hover:opacity-90 transition-opacity">
                  注册
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <AlertDialog open={loginAlert} onOpenChange={setLoginAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>需要登录</AlertDialogTitle>
            <AlertDialogDescription>请先登录后再访问此功能</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={() => navigate('/login')}>去登录</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
